"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type GreekPeriod,
  type GreekSystem,
  greekLetterRows,
  type IotaSubscript,
  SYSTEMS_FOR_PERIOD,
  transliterateGreek,
} from "@/lib/tool-kit/converters/greek";

/** English strings. A translated content file overrides these through `ui`. */
export const GREEK_TRANSLITERATOR_STRINGS = {
  period: "Kind of Greek",
  modern: "Modern Greek (after 1453)",
  ancient: "Ancient & Medieval Greek (before 1454)",
  inputLabel: "Greek text",
  inputHint:
    "Monotonic or polytonic. Breathings, accents and iota subscripts are read from the text.",
  samples: {
    modern: "Νίκος Καζαντζάκης, Βίος και πολιτεία του Αλέξη Ζορμπά",
    ancient: "Ἡροδότου Ἁλικαρνησσέος ἱστορίης ἀπόδεξις",
  },
  iotaLabel: "Iota subscript (ᾳ ῃ ῳ) in the classical system",
  iotaOmit: "Leave it out (SBL, ALA-LC)",
  iotaWrite: "Write it as i after the vowel (Chicago)",
  resultsHeading: "Romanized",
  systems: {
    elot: "ELOT 743 (UN, BGN/PCGN)",
    "ala-lc-modern": "ALA-LC (Library of Congress), Modern",
    classical: "Classical (SBL, Chicago)",
    "ala-lc-ancient": "ALA-LC (Library of Congress), Ancient & Medieval",
  },
  uses: {
    elot: "Greece's national standard, approved by the UN for place names. Marks the stress with an acute.",
    "ala-lc-modern": "Library catalogs and English-language bibliographies.",
    classical: "Classics and biblical studies.",
    "ala-lc-ancient": "Library catalogs, for texts written before 1454.",
  },
  supplyNote:
    "ALA-LC writes h for a rough breathing even when the text leaves it out, but the converter can only know that for words beginning with υ (or ρ in Ancient Greek). For other words in monotonic or capital-letter text, add the h yourself: Ελληνικό is Hellēniko.",
  enterText: "Type or paste some Greek to romanize.",
  copy: "Copy",
  copied: "Copied!",
  tableHeading: "Letter by letter",
  tableSystem: "System for the letter table",
  letterColumn: "Greek",
  latinColumn: "Romanization",
  noteColumn: "Note",
  notes: {
    "gamma-nasal": "γ before γ, κ, ξ or χ is n (the gamma nasal)",
    "gk-edges": "gk at the start or end of a word, nk inside it",
    "gk-always": "always gk",
    "mp-initial": "b at the start of a word, mp elsewhere",
    "mp-edges": "b at the start and end of a word, mp inside it",
    "nt-initial": "d̲ (underlined d) at the start of a word, nt elsewhere",
    "upsilon-diphthong": "υ is u in these diphthongs and y everywhere else",
    "elot-av":
      "v before a vowel or β, γ, δ, ζ, λ, μ, ν, ρ; f before θ, κ, ξ, π, σ, τ, φ, χ, ψ and at the end of a word; ay, ey, iy when the first vowel is stressed or υ has a diaeresis",
    "elot-ou": "oy when ο is stressed or υ has a diaeresis",
    "rough-h": "rough breathing: h before the vowel or diphthong",
    "rho-rh": "ρ with a rough breathing; in Ancient Greek, every initial ρ",
    "rho-rh-classical":
      "every initial ρ, and the second ρ of ρρ (Πύρρος → Pyrrhos)",
    "beta-ancient": "b in Ancient and Medieval Greek, v in Modern Greek",
    "iota-subscript":
      "ALA-LC and SBL leave it out; Chicago writes i after the vowel",
    archaic: "archaic letters digamma and koppa",
  },
} as const;

type Strings = typeof GREEK_TRANSLITERATOR_STRINGS;

function LetterTable({
  period,
  strings,
}: {
  period: GreekPeriod;
  strings: Strings;
}) {
  const systems = SYSTEMS_FOR_PERIOD[period];
  const [chosen, setChosen] = useState<GreekSystem>(systems[0]);
  const system = systems.includes(chosen) ? chosen : systems[0];
  return (
    <section className={`${CARD} space-y-4`}>
      <h2 className={WIDGET_H2}>{strings.tableHeading}</h2>
      <div className="max-w-sm">
        <SelectField
          id="gr-table-system"
          label={strings.tableSystem}
          onChange={setChosen}
          options={systems.map((each) => ({
            value: each,
            label: strings.systems[each],
          }))}
          value={system}
        />
      </div>
      <DataTable
        head={[strings.letterColumn, strings.latinColumn, strings.noteColumn]}
        rows={greekLetterRows(system).map((row) => [
          row.greek,
          row.latin,
          row.note ? strings.notes[row.note] : "",
        ])}
      />
    </section>
  );
}

export default function GreekTransliteratorWidget() {
  const strings = useToolUi(GREEK_TRANSLITERATOR_STRINGS);
  const [period, setPeriod] = useState<GreekPeriod>("modern");
  const [text, setText] = useState<string>(strings.samples.modern);
  const [iota, setIota] = useState<IotaSubscript>("omit");

  function changePeriod(next: GreekPeriod) {
    // Swap the sample for the other period's, but never overwrite the reader's own text.
    const isSample =
      text === strings.samples.modern || text === strings.samples.ancient;
    setPeriod(next);
    if (isSample || text.trim() === "") {
      setText(strings.samples[next]);
    }
  }

  const systems = SYSTEMS_FOR_PERIOD[period];

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.period}
        name="gr-period"
        onChange={changePeriod}
        options={[
          { value: "modern", label: strings.modern },
          { value: "ancient", label: strings.ancient },
        ]}
        value={period}
      />
      <TextAreaField
        hint={strings.inputHint}
        id="gr-input"
        label={strings.inputLabel}
        onChange={setText}
        prose
        rows={4}
        value={text}
      />
      {period === "ancient" ? (
        <div className="max-w-md">
          <SelectField
            id="gr-iota"
            label={strings.iotaLabel}
            onChange={setIota}
            options={[
              { value: "omit", label: strings.iotaOmit },
              { value: "i", label: strings.iotaWrite },
            ]}
            value={iota}
          />
        </div>
      ) : null}
      {text.trim() === "" ? (
        <Notice>{strings.enterText}</Notice>
      ) : (
        <section aria-live="polite" className="space-y-3">
          <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {systems.map((system) => {
              const output = transliterateGreek(text, system, {
                iotaSubscript: iota,
              });
              return (
                <div className={`${CARD} space-y-2`} key={system}>
                  <h3 className={WIDGET_H3}>{strings.systems[system]}</h3>
                  <p className={HINT}>{strings.uses[system]}</p>
                  <p className="whitespace-pre-wrap break-words text-foreground text-lg leading-relaxed">
                    {output}
                  </p>
                  <CopyButton
                    copiedLabel={strings.copied}
                    label={strings.copy}
                    small
                    text={output}
                  />
                </div>
              );
            })}
          </div>
          <p className={HINT}>{strings.supplyNote}</p>
        </section>
      )}
      <LetterTable period={period} strings={strings} />
    </div>
  );
}
